import { useInfiniteQuery } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import useHttpClient from './useHttpClient';

export interface IComment {
    projectId: {
        native: number;
    },
    commentId: {
        native: number;
    },
    noteId: {
        native: number;
        partner: number;
    },
    body: string;
    createdAt: string;
    authorId: {
        native: number;
    },
    allowEditing: true,
    documents: [],
}

export interface INoteComments {
  hasMore: boolean;
  items: IComment[];
}

export interface INoteCommentsPayload {
  commentsCalled?: boolean;
  commentsLoading: boolean;
  commentsErr?: Error;
  comments?: INoteComments;
  getNoteComments: (noteId: number) => void;
  getMoreNoteComments: () => Promise<void> | undefined;
  refreshNoteComments: () => Promise<void> | undefined;
}

const useQueryNoteComments = (): INoteCommentsPayload => {
  const httpClient = useHttpClient();
  const [noteId, setNoteId] = useState<number | null>(null);

  const {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    refetch
  } = useInfiniteQuery({
    queryKey: ['noteComments', noteId],
    queryFn: async ({ pageParam = 0 }) => {
      if (!noteId) {
        throw new Error('Note ID not set');
      }
      const params = new URLSearchParams({
        offset: pageParam.toString(),
        orderByDescending: 'true',
        limit: '50',
      });
      const response = await httpClient.get(`/notes/${noteId}/comments?${params.toString()}`);
      return response.data as INoteComments;
    },
    getNextPageParam: (lastPage: INoteComments, allPages) => {
      return lastPage.hasMore ? allPages.reduce((total, page) => total + page.items.length, 0) : undefined;
    },
    enabled: noteId !== null,
  });

  const getNoteComments = useCallback((id: number) => {
    setNoteId(id);
  }, []);

  const getMoreNoteComments = useCallback(async () => {
    if (hasNextPage && !isLoading) {
      await fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isLoading]);

  const refreshNoteComments = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Flatten all pages into a single comments object
  const allComments = data?.pages.reduce((acc, page) => ({
    hasMore: page.hasMore,
    items: [...acc.items, ...page.items],
  }), { hasMore: false, items: [] as IComment[] });

  return {
    commentsCalled: noteId !== null,
    commentsErr: error as Error,
    commentsLoading: isLoading,
    comments: allComments,
    getNoteComments,
    getMoreNoteComments,
    refreshNoteComments,
  };
};

export default useQueryNoteComments;